<template>
  <div v-if="!unfinishedRegistrationAndHasApplication" v-qa="'calculator'" :class="{ disabled, wide, full, editable }" class="calculator">
    <calculator-tabs :limit="limit"/>

    <div
      v-if="!unfinishedRegistrationAndHasApplication"
      class="calculator__top sf-ph-s sf-pv-s"
      :class="{'bottom-padding': isButtonVisible}">
      <sf-calculator
        ref="sliders"
        :show-term-stl="isTermVisible"
        class="calculator__sliders"
        v-bind="{ mode, limit, colors }"
        @edit="updateEdit">
        <template slot="edit-loan">{{ $t('calculator.edit-loan') }}</template>
        <template slot="loan-amount">{{ $t('calculator.loan-amount') }}</template>
        <template slot="loan-days">{{ $t('calculator.loan-days') }}</template>
        <template slot="days-select">{{ $t('calculator.days') }}</template>
      </sf-calculator>

      <sf-slide-toggle :value="aboveAvailableAmount" class="calculator__tooltip">
        <div class="sf-mt-s sf-ta-c">
          <nuxt-link :to="localePath('login')">{{ $t('calculator.repeated-user') }}</nuxt-link>
        </div>
      </sf-slide-toggle>

      <div class="calculator__button">
        <slot name="button">
          <app-button
            v-if="isButtonVisible"
            v-qa="'btn_calculator_submit'"
            :disabled="disabled"
            :url="url"
            class="block secondary">
            <span class="button-text">
              <template v-if="aboveAvailableAmount">{{ $t('calculator.log-in') }}</template>
              <template v-else>{{ $t('calculator.submit') }}</template>
            </span>
          </app-button>
        </slot>
      </div>
    </div>


    <div class="calculator__bottom" :class="{ 'top-padding': isButtonVisible }">
      <calculator-stats
        :with-insurance="withInsurance"
        :with-date="isApplicationRoute"
        :is-application-info="unfinishedRegistrationAndHasApplication"/>

      <div v-if="withTerms" class="terms">
        {{ $t('calculator.terms') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { WEB_ROUTES } from '~/utils/webConfig';
import CalculatorStats from '~/components/calculator/stats.vue';
import CalculatorTabs from '~/components/calculator/tabs.vue';
import AppButton from '~/components/app/app-button.vue';

const REGISTRATION_STEPS = [
  WEB_ROUTES.REGISTRATION_STEP_2,
  WEB_ROUTES.REGISTRATION_STEP_3,
  WEB_ROUTES.REGISTRATION_STEP_4,
];

const APPLICATION_ROUTES = [
  WEB_ROUTES.REGISTRATION_STEP_6,
  WEB_ROUTES.APPLY,
  WEB_ROUTES.APPLICATION_OFFER_INCREASE,
];

export default {
  components: { AppButton, CalculatorTabs, CalculatorStats },
  props: {
    mode: {
      type: String,
      default: 'full', // full || edit
    },
    limit: {
      type: String,
      default: '',
    },
    wide: {
      type: Boolean,
      default: false,
    },
    withInsurance: {
      type: Boolean,
      default: false,
    },
    withButton: {
      type: Boolean,
      default: true,
    },
    withTerms: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editable: this.mode === 'full',
      colors: {
        process: this.$theme.colors.primary,
        arrows: this.$theme.colors['gray-darker'],
        dot: this.$theme.colors.secondary,
        processAlt: this.$theme.colors.primary,
        arrowsAlt: this.$theme.colors.white,
        dotAlt: this.$theme.colors['gray-darker'],
      },
      newClientInterestDiscount: 0,
      repeatedTotal: 0,
      repeatedInterest: 0,
    };
  },
  computed: {
    ...mapGetters('arithmetic', [
      'amount',
      'disabled',
      'authorized',
      'first',
      'repeated',
      'displayed',
      'term',
    ]),
    routeName() {
      return (this.$route.name || '').split('___')[0];
    },
    registrationSteps() {
      return REGISTRATION_STEPS.includes(this.routeName);
    },
    unfinishedRegistrationAndHasApplication() {
      return Boolean(this.registrationSteps && this.$client.profile.hasUncancelledApplications);
    },
    aboveAvailableAmount() {
      if (this.disabled || this.authorized) return false;
      return this.amount.current > this.amount.first;
    },
    tab() {
      return this.$store.state.arithmetic.current.tab;
    },
    sliderTerm() {
      return this.$store.state.arithmetic.current.term;
    },
    sliderAmount() {
      return this.$store.state.arithmetic.current.amount;
    },
    full() {
      return this.mode === 'full';
    },
    url() {
      if (this.authorized) {
        return this.localePath(WEB_ROUTES.APPLY);
      }

      if (this.amount.current > this.amount.first) {
        return this.localePath(WEB_ROUTES.LOGIN);
      }

      return this.localePath(WEB_ROUTES.REGISTRATION_STEP_1);
    },
    isButtonVisible() {
      return this.withButton && this.full;
    },
    isApplicationRoute() {
      return APPLICATION_ROUTES.includes(this.routeName);
    },
    isTermVisible() {
      return (
        this.isApplicationRoute &&
        this.$sr('$client.application.status') !== this.$tools.status.APPLICATION_YHAT_INCREASE
      );
    },
  },
  methods: {
    updateEdit(editable) {
      this.editable = !!editable;
    },
    refresh() {
      this.$refs.sliders.refresh();
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator {
  position: relative;
  width: 100%;
  outline: none;
  overflow: hidden;
  box-shadow: -2px 4px 20px rgba(#000, 0.15);
  color: map-get($colors, gray-darker);

  @include tablet {
    border-radius: 10px;
    width: 375px;
  }

  &.wide {
    @include not-desktop {
      width: 100%;
    }
  }

  &.disabled {
    &:after {
      @extend %overlay;
      position: absolute;
      z-index: 1;
      background: rgba(#fff, 0.5);
      content: '';

      @include tablet {
        border-radius: 10px;
      }
    }
  }

  &.full {
    .calculator__top {
      padding-bottom: map-get($paddings, normal);
    }

    .calculator__stats {
      padding-top: 45px;
    }
  }

  &:not(.full) {
    .calculator__tooltip {
      display: none;
    }
  }

  &:not(.editable) {
    @include mobile {
      .calculator__stats {
        display: none;
      }
    }
  }

  &__top {
    position: relative;
    background: map-get($colors, white);
  }

  &__tabs {
    color: #fff;
  }

  &__bottom {
    background: map-get($colors, white);
    color: map-get($colors, gray-darker);
  }

  &__tooltip a {
    color: inherit;
  }

  &__button {
    position: absolute;
    bottom: 0;
    z-index: 5;
    transform: translateY(50%);

    @include mobile {
      left: map-get($paddings, smallM);
      right: map-get($paddings, smallM);
    }

    @include tablet {
      left: map-get($paddings, small);
      right: map-get($paddings, small);
    }
  }

  /deep/ .edit {
    padding: 0;
  }
}

.button-text {
  font-size: 16px;
}

/deep/ .vue-slider-component .vue-slider-dot .vue-slider-dot-handle {
  box-shadow: none;
}

/deep/ .slider__label {
  font-size: 14px;
  font-weight: 500;
}

.terms {
  padding: 16px;
  text-align: left;
  font-size: 14px;
}
</style>
